<template>
  <div class="app is-loading">
    <expiration-overlay
      :lock-username="lockScreenUsername"
      :username="username"
      :auth-url="authUrl"
      :identity-provider="identityProvider"
      :token="token"
      :user-id="userId"
      :account-id="accountId"
      :cookies-expired="cookiesExpired"
      :cookies-another-user="cookiesAnotherUser"
      :login-overlay-disabled="false"
      @onUpdateCookieExpiration="cookiesRenewHandler"
    />
    <app-page
      :token="token"
      :config="config"
      :account-id="accountId"
      :user-role="role"
    >
      <router-view />
    </app-page>
  </div>
</template>

<script setup lang="ts">
import { onErrorCaptured, provide, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  AUTH_API_INJECTION_TOKEN,
  ACCOUNTS_API_INJECTION_TOKEN,
  USERS_API_INJECTION_TOKEN,
  APP_CONFIG,
  ExpirationOverlay,
} from '@onereach/auth-ui-module';
import { Auth, AccountsApi, UsersApi } from '@/api';
import config from '@/config';
import { AppPage } from '@/components';
import { storeToRefs } from 'pinia';
import { useAuth } from './stores';
import SentryApi from '@/sentry';

const router = useRouter();
const authStore = useAuth();
const {
  authorized: isAuthorized,
  username,
  identityProvider,
  token,
  cookiesAnotherUser,
  cookiesExpired,
  userId,
  multiUserId,
  accountId,
  role,
} = storeToRefs(authStore);
const {
  getUserNameForLockScreen,
} = authStore;
const authUrl = config.AUTH_UI_URL;
const lockScreenUsername = ref('');

//provide api services to AuthPopover
provide(AUTH_API_INJECTION_TOKEN, Auth);
provide(ACCOUNTS_API_INJECTION_TOKEN, AccountsApi);
provide(USERS_API_INJECTION_TOKEN, UsersApi);
provide(APP_CONFIG, config);

watch(isAuthorized, async (value): Promise<void> => {
  if (value) {
    lockScreenUsername.value = await getUserNameForLockScreen();
  }
});

onErrorCaptured((e: any) => {
  if (e.status === 404) {
    router.push({ path: '/notFound' });
    return;
  }
  SentryApi.captureException(e);
  console.error(e);
  return false;
});

function cookiesRenewHandler(): void {
  Auth.updateCookieExpiration();
}
</script>

<style lang="scss">
html,
body,
#app,
.app {
  height: 100%;
}
</style>
